import React, { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../AuthContext";
import {
  CONTEST_END, CONTEST_PRIZE, CONTEST_START, TYPE_LABEL, linkFor, partnerLinkFor, partnerQrFor, qrFor, slug, typeOf,
} from "../shared/referrals";

// Admin: where customers come from, the bar contest, and making new
// sign-up links (employees, Facebook, flyers...) with QR codes.

const box = { background: "#0D0B1A", padding: "20px", borderRadius: "12px", marginBottom: "20px" };
const input = {
  minHeight: "44px", padding: "0 12px", borderRadius: "10px", border: "1px solid #3A3350",
  background: "#17131F", color: "#fff", fontSize: "15px", boxSizing: "border-box",
};
const btn = { minHeight: "44px", padding: "0 16px", borderRadius: "10px", border: "none", fontWeight: 700, cursor: "pointer" };

const KIND_PREFIX = { employee: "emp-", campaign: "", bar: "bar-" };

function toDate(ts) {
  return ts && typeof ts.toDate === "function" ? ts.toDate() : null;
}

export default function AdminSignups() {
  const { role } = useAuth();
  const [codes, setCodes] = useState([]); // referralCodes docs
  const [customers, setCustomers] = useState([]); // users with signupRef (+ totals)
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("employee");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [msg, setMsg] = useState("");
  const [qrShown, setQrShown] = useState({}); // code -> dataUrl
  const [partnerQrShown, setPartnerQrShown] = useState({}); // code -> dataUrl (owner sign-up)
  const [partners, setPartners] = useState([]); // owner/charity accounts

  const load = async () => {
    setLoading(true);
    const [codesSnap, usersSnap] = await Promise.all([
      getDocs(collection(db, "referralCodes")),
      getDocs(query(collection(db, "users"))),
    ]);
    setCodes(codesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    const all = usersSnap.docs.map((d) => d.data()).filter((u) => !["owner", "charity", "admin"].includes(u.role));
    setTotalCustomers(all.length);
    setPartners(
      usersSnap.docs
        .map((d) => d.data())
        .filter((u) => ["owner", "charity"].includes(u.role))
        .map((u) => ({
          email: u.email || "",
          role: u.role,
          ref: u.signupRef || "",
          type: u.signupRef ? u.signupRefType || typeOf(u.signupRef) : "none",
          at: toDate(u.signupRefAt) || toDate(u.createdAt) || (u.createdAt instanceof Date ? u.createdAt : null),
        }))
    );
    setCustomers(all.filter((u) => u.signupRef).map((u) => ({ ref: u.signupRef, type: u.signupRefType || typeOf(u.signupRef), at: toDate(u.signupRefAt) })));
    setLoading(false);
  };

  useEffect(() => {
    if (role === "admin") load().catch((e) => { setMsg(e.message); setLoading(false); });
  }, [role]);

  useEffect(() => {
    if (!codeEdited) setCode(name ? `${KIND_PREFIX[kind]}${slug(name, 30)}` : "");
  }, [name, kind, codeEdited]);

  if (role !== "admin") return <p style={{ color: "#fff" }}>Admins only.</p>;

  const signupsFor = (c) => customers.filter((u) => u.ref === c).length;
  const contestSignupsFor = (c) =>
    customers.filter((u) => u.ref === c && u.at && u.at >= CONTEST_START && u.at <= CONTEST_END).length;

  // Bars in the contest: every bar code that exists or has sign-ups.
  const barCodes = [...new Set([...codes.filter((c) => typeOf(c.id) === "bar").map((c) => c.id), ...customers.filter((u) => u.type === "bar").map((u) => u.ref)])];
  const labelFor = (c) => codes.find((x) => x.id === c)?.label || c;
  const leaderboard = barCodes
    .map((c) => ({ code: c, label: labelFor(c), n: contestSignupsFor(c) }))
    .filter((r) => r.n > 0)
    .sort((a, b) => b.n - a.n);

  const byType = ["bar", "employee", "campaign"].map((t) => ({ t, n: customers.filter((u) => u.type === t).length }));
  const noLink = totalCustomers - customers.length;

  const create = async (e) => {
    e.preventDefault();
    setMsg("");
    const c = slug(code, 60);
    if (!c || !name.trim()) return setMsg("Enter a name.");
    if (kind === "employee" && !c.startsWith("emp-")) return setMsg('Employee codes must start with "emp-".');
    if (kind === "bar" && !c.startsWith("bar-")) return setMsg('Bar codes must start with "bar-".');
    if (kind === "campaign" && (c.startsWith("emp-") || c.startsWith("bar-"))) return setMsg('Campaign codes can\'t start with "emp-" or "bar-".');
    const ref = doc(db, "referralCodes", c);
    const existing = await getDoc(ref);
    if (existing.exists() && existing.data().label) return setMsg(`"${c}" is already used for ${existing.data().label}. Pick another.`);
    await setDoc(ref, { code: c, type: typeOf(c), label: name.trim(), createdAt: serverTimestamp() }, { merge: true });
    setName("");
    setCodeEdited(false);
    setMsg(`Created. Link: ${linkFor(c)}`);
    load();
  };

  const toggleQr = async (c) => {
    if (qrShown[c]) return setQrShown((p) => ({ ...p, [c]: null }));
    const url = await qrFor(c);
    setQrShown((p) => ({ ...p, [c]: url }));
  };

  const togglePartnerQr = async (c) => {
    if (partnerQrShown[c]) return setPartnerQrShown((p) => ({ ...p, [c]: null }));
    const url = await partnerQrFor(c);
    setPartnerQrShown((p) => ({ ...p, [c]: url }));
  };
  const partnerSignupsFor = (c) => partners.filter((p) => p.ref === c).length;
  const partnerByType = ["bar", "employee", "campaign", "none"].map((t) => ({ t, n: partners.filter((p) => p.type === t).length }));
  const recentPartners = partners
    .filter((p) => p.ref)
    .sort((a, b) => (b.at?.getTime?.() || 0) - (a.at?.getTime?.() || 0))
    .slice(0, 25);

  const copy = (text) => navigator.clipboard?.writeText(text).catch(() => window.prompt("Copy this link:", text));

  const sortedCodes = [...codes].sort((a, b) => signupsFor(b.id) - signupsFor(a.id) || (b.scans || 0) - (a.scans || 0));
  const th = { textAlign: "left", padding: "8px", color: "#B8B0C8", fontSize: "14px", whiteSpace: "nowrap" };
  const td = { padding: "8px", fontSize: "15px", verticalAlign: "top" };

  return (
    <div style={{ color: "#fff", maxWidth: "1000px" }}>
      <h2 style={{ margin: "0 0 6px" }}>Where customers come from</h2>
      <p style={{ margin: "0 0 20px", color: "#B8B0C8" }}>
        Every sign-up link is <code>{"app.barfunfinder.com/?ref=CODE"}</code>. New accounts are stamped with the code they came from.
      </p>
      {msg && <p style={{ color: msg.startsWith("Created") ? "#4ADE80" : "#FF6B8A", wordBreak: "break-all" }}>{msg}</p>}
      {loading ? (
        <p style={{ color: "#B8B0C8" }}>Loading…</p>
      ) : (
        <>
          <div style={box}>
            <h3 style={{ margin: "0 0 10px" }}>Customers by source</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
              {[...byType.map((x) => ({ label: `${TYPE_LABEL[x.t]} links`, n: x.n })), { label: "No link (came on their own)", n: noLink }].map((c) => (
                <div key={c.label} style={{ background: "#17131F", borderRadius: "10px", padding: "14px" }}>
                  <div style={{ fontSize: "28px", fontWeight: 700 }}>{c.n}</div>
                  <div style={{ color: "#B8B0C8", fontSize: "14px" }}>{c.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={box}>
            <h3 style={{ margin: "0 0 4px" }}>Bar owners & charities by source</h3>
            <p style={{ margin: "0 0 12px", color: "#B8B0C8" }}>
              From the "Owner link" or "Owner QR" below (portal.barfunfinder.com/signup?ref=CODE).
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
              {partnerByType.map((x) => (
                <div key={x.t} style={{ background: "#17131F", borderRadius: "10px", padding: "14px" }}>
                  <div style={{ fontSize: "28px", fontWeight: 700 }}>{x.n}</div>
                  <div style={{ color: "#B8B0C8", fontSize: "14px" }}>{x.t === "none" ? "No link (came on their own)" : `${TYPE_LABEL[x.t]} links`}</div>
                </div>
              ))}
            </div>
            {recentPartners.length > 0 && (
              <div style={{ marginTop: "14px" }}>
                <p style={{ margin: "0 0 6px", fontWeight: 600 }}>Latest from a link</p>
                {recentPartners.map((p, i) => (
                  <p key={i} style={{ margin: "2px 0", color: "#D6D2E4", fontSize: "15px" }}>
                    {p.email || "(no email)"} · {p.role === "owner" ? "Owner" : "Charity"} · from <strong>{labelFor(p.ref)}</strong>
                    {p.at ? ` · ${p.at.toLocaleDateString()}` : ""}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div style={box}>
            <h3 style={{ margin: "0 0 4px" }}>Bar contest leaderboard</h3>
            <p style={{ margin: "0 0 12px", color: "#B8B0C8" }}>
              Sign-ups from each bar's own link/QR, {CONTEST_START.toLocaleDateString()} – {CONTEST_END.toLocaleDateString()}. Prize: {CONTEST_PRIZE}.
            </p>
            {leaderboard.length === 0 ? (
              <p style={{ color: "#B8B0C8", margin: 0 }}>No bar sign-ups yet.</p>
            ) : (
              <ol style={{ margin: 0, paddingLeft: "22px", lineHeight: 1.9, fontSize: "16px" }}>
                {leaderboard.map((r) => (
                  <li key={r.code}>
                    <strong>{r.label}</strong> · {r.n} {r.n === 1 ? "customer" : "customers"}
                  </li>
                ))}
              </ol>
            )}
          </div>

          <form onSubmit={create} style={box}>
            <h3 style={{ margin: "0 0 12px" }}>Make a new sign-up link</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
              <label style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontWeight: 600 }}>For</span>
                <select value={kind} onChange={(e) => { setKind(e.target.value); setCodeEdited(false); }} style={input}>
                  <option value="employee">An employee</option>
                  <option value="campaign">Facebook / ad / flyer / other</option>
                  <option value="bar">A bar (not using the dashboard yet)</option>
                </select>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 200px" }}>
                <span style={{ fontWeight: 600 }}>Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === "employee" ? "Sarah" : kind === "bar" ? "Wild Bill's - Woodbury" : "Facebook October post"} style={input} />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 200px" }}>
                <span style={{ fontWeight: 600 }}>Code</span>
                <input value={code} onChange={(e) => { setCode(e.target.value); setCodeEdited(true); }} style={input} />
              </label>
              <button type="submit" style={{ ...btn, background: "#FF3D81", color: "#0B0B12" }}>Create link</button>
            </div>
          </form>

          <div style={box}>
            <h3 style={{ margin: "0 0 10px" }}>All links</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "860px" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #241E33" }}>
                    <th style={th}>Name</th>
                    <th style={th}>Type</th>
                    <th style={th}>Code</th>
                    <th style={{ ...th, textAlign: "right" }}>Scans</th>
                    <th style={{ ...th, textAlign: "right" }}>Customers</th>
                    <th style={{ ...th, textAlign: "right" }}>Owners</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCodes.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr style={{ borderBottom: "1px solid #17131F" }}>
                        <td style={td}>{c.label || "—"}</td>
                        <td style={td}>{TYPE_LABEL[typeOf(c.id)]}</td>
                        <td style={{ ...td, fontFamily: "monospace", fontSize: "14px" }}>{c.id}</td>
                        <td style={{ ...td, textAlign: "right" }}>{c.scans || 0}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{signupsFor(c.id)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{partnerSignupsFor(c.id)}</td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          <button type="button" onClick={() => copy(linkFor(c.id))} style={{ ...btn, background: "transparent", color: "#B9A6FF", padding: "0 8px" }}>Copy link</button>
                          <button type="button" onClick={() => toggleQr(c.id)} style={{ ...btn, background: "transparent", color: "#FF6FA3", padding: "0 8px" }}>{qrShown[c.id] ? "Hide QR" : "QR"}</button>
                          <button type="button" onClick={() => copy(partnerLinkFor(c.id))} style={{ ...btn, background: "transparent", color: "#B9A6FF", padding: "0 8px" }}>Owner link</button>
                          <button type="button" onClick={() => togglePartnerQr(c.id)} style={{ ...btn, background: "transparent", color: "#FF6FA3", padding: "0 8px" }}>{partnerQrShown[c.id] ? "Hide owner QR" : "Owner QR"}</button>
                        </td>
                      </tr>
                      {partnerQrShown[c.id] && (
                        <tr>
                          <td colSpan={7} style={{ ...td, paddingBottom: "16px" }}>
                            <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                              <img src={partnerQrShown[c.id]} alt={`Owner sign-up QR code for ${c.label || c.id}`} style={{ width: "160px", height: "160px", background: "#fff", borderRadius: "8px", padding: "6px" }} />
                              <div>
                                <div style={{ fontWeight: 700, marginBottom: "4px" }}>For bar owners and charities</div>
                                <div style={{ wordBreak: "break-all", color: "#D6D2E4", marginBottom: "8px" }}>{partnerLinkFor(c.id)}</div>
                                <a href={partnerQrShown[c.id]} download={`${c.id}-owner-qr.png`} style={{ color: "#FF6FA3", fontWeight: 700 }}>Download owner QR (PNG)</a>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {qrShown[c.id] && (
                        <tr>
                          <td colSpan={7} style={{ ...td, paddingBottom: "16px" }}>
                            <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
                              <img src={qrShown[c.id]} alt={`QR code for ${c.label || c.id}`} style={{ width: "160px", height: "160px", background: "#fff", borderRadius: "8px", padding: "6px" }} />
                              <div>
                                <div style={{ wordBreak: "break-all", color: "#D6D2E4", marginBottom: "8px" }}>{linkFor(c.id)}</div>
                                <a href={qrShown[c.id]} download={`${c.id}-qr.png`} style={{ color: "#FF6FA3", fontWeight: 700 }}>Download QR (PNG)</a>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
